/**
 * Sign Document Page
 *
 * Main document signing page for the signer portal.
 * Features:
 * - Token validation
 * - Document viewing with PDF renderer
 * - Signature capture modal
 * - Apply signatures to document
 * - Decline option with reason
 * - Confirmation with download
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  X,
  AlertTriangle,
  Send,
} from 'lucide-react';
import { Button, Card, CardContent, Spinner } from '@respectly/ui';
import { executeRecaptcha } from '@respectly/utils';
import { PortalLayout } from '../layouts/PortalLayout';
import { DocumentViewer } from '../components/DocumentViewer';
import { SignatureCapture } from '../components/SignatureCapture';
import { SignerAcknowledgment } from '../components/SignerAcknowledgment';
import { ContactSupportModal } from '../components/ContactSupportModal';
import { SignerFieldList, type SignerFieldValue } from '../components/SignerFieldList';
import { SignerInfoPanel } from '../components/SignerInfoPanel';
import { MobileSigningBar } from '../components/MobileSigningBar';
import { MobileSigningDrawer } from '../components/MobileSigningDrawer';
import { ExpiredPage } from './ExpiredPage';
import { CompletedPage } from './CompletedPage';
import { NotFoundPage } from './NotFoundPage';
import { LoadingPage } from './LoadingPage';

interface SignatureField {
  id: string;
  label: string;
  required: boolean;
  /** Determines how this field is rendered: 'signature' | 'initials' | 'text' | 'checkbox' | 'dropdown' | 'image' */
  fieldType?: string;
  position?: {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
  };
  // Editor-configured properties — see DocumentFieldPropertiesPanel.
  placeholder?: string;
  helpText?: string;
  defaultValue?: string | boolean;
  options?: Array<{ label: string; value: string }>;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    customMessage?: string;
  };
}

// Unified field value model — shared with SignerFieldList and DocumentViewer.
type SignatureData = SignerFieldValue;

interface TextField {
  id: string;
  variableName: string;
  value: string;
  position: {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

interface SignerContext {
  role: string;
  order: number;
  totalSigners: number;
}

interface DocumentData {
  signatureId: string;
  status: string;
  document: {
    id: string;
    name: string;
    fileUrl: string;
    pageCount: number;
    signatureFields: SignatureField[];
    textFields?: TextField[];
  };
  contact: {
    firstName: string;
    lastName?: string;
    email?: string;
  };
  expiresAt?: string;
  signerContext?: SignerContext; // Multi-signer context
}

// API base URL for backend requests
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function SignDocument() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenStatus, setTokenStatus] = useState<string | null>(null);
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);

  // Signature capture state
  const [activeField, setActiveField] = useState<SignatureField | null>(null);
  const [signedFields, setSignedFields] = useState<Map<string, SignatureData>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  // Decline state
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [isDeclined, setIsDeclined] = useState(false);

  // Acknowledgment state
  const [showAcknowledgment, setShowAcknowledgment] = useState(true);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);

  // Mobile drawer state (only used on <lg viewports)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Get signature ID and token from URL
  const getUrlParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const pathParts = window.location.pathname.split('/');
    const signatureId = pathParts[pathParts.indexOf('sign') + 1];
    const token = params.get('token') || signatureId; // Use signature ID as token if not provided
    return { signatureId, token };
  }, []);

  // Fetch document data
  useEffect(() => {
    const fetchDocument = async () => {
      const { signatureId, token } = getUrlParams();

      if (!signatureId) {
        setTokenStatus('not_found');
        setIsLoading(false);
        return;
      }

      try {
        // First validate the token
        const validateResponse = await fetch(
          `${API_BASE}/public/sign/${signatureId}/validate?token=${token}`
        );
        const validateResult = await validateResponse.json();

        if (!validateResponse.ok || !validateResult.success) {
          setError(validateResult.error?.message || 'Failed to validate token');
          setIsLoading(false);
          return;
        }

        const status = validateResult.data?.status;
        if (status !== 'valid') {
          setTokenStatus(status);
          setIsLoading(false);
          return;
        }

        // Fetch document details — include the signer's local timezone so the
        // server can render date variables ({{date}}, {{today}}, etc.) correctly.
        const signerTz = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
        const docResponse = await fetch(
          `${API_BASE}/public/sign/${signatureId}?token=${token}&tz=${signerTz}`
        );
        const docResult = await docResponse.json();

        if (!docResponse.ok || !docResult.success) {
          if (docResponse.status === 404) {
            setTokenStatus('not_found');
          } else if (docResponse.status === 410) {
            setTokenStatus(docResult.error?.code?.toLowerCase() || 'expired');
          } else {
            setError(docResult.error?.message || 'Failed to load document');
          }
          setIsLoading(false);
          return;
        }

        setDocumentData(docResult.data);
        setTokenStatus('valid');
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching document:', err);
        setError('Failed to connect to server. Please try again.');
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [getUrlParams]);

  // Handle signature capture
  const handleSignatureFieldClick = (field: SignatureField) => {
    setActiveField(field);
  };

  const handleSignatureSave = (signatureData: SignatureData) => {
    if (!activeField) return;

    setSignedFields((prev) => {
      const newMap = new Map(prev);
      newMap.set(activeField.id, signatureData);
      return newMap;
    });
    setActiveField(null);
  };

  // Check if all required fields are signed
  const allRequiredFieldsSigned = useCallback(() => {
    if (!documentData) return false;

    const requiredFields = documentData.document.signatureFields.filter(
      (f) => f.required
    );
    return requiredFields.every((field) => signedFields.has(field.id));
  }, [documentData, signedFields]);

  // Submit signature
  const handleSubmit = async () => {
    if (!documentData || !allRequiredFieldsSigned()) return;

    const { signatureId, token } = getUrlParams();
    setIsSubmitting(true);

    try {
      // Get the primary signature (first one in the map)
      const primarySignature = signedFields.values().next().value;
      const recaptchaToken = await executeRecaptcha('SIGN_DOCUMENT');

      const response = await fetch(`${API_BASE}/public/sign/${signatureId}?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signatureData: primarySignature,
          signedFields: Array.from(signedFields.entries()).map(([fieldId, sig]) => ({
            fieldId,
            signatureData: sig,
          })),
          // Include signer's timezone for audit trail and date variable resolution
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          recaptchaToken,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error?.message || 'Failed to submit signature');
        setIsSubmitting(false);
        return;
      }

      setDownloadUrl(result.data?.downloadUrl || null);
      setIsSuccess(true);
      setIsSubmitting(false);
    } catch (err) {
      console.error('Error submitting signature:', err);
      setError('Failed to submit signature. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Handle decline
  const handleDecline = async () => {
    if (!declineReason.trim()) return;

    const { signatureId, token } = getUrlParams();
    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${API_BASE}/public/sign/${signatureId}/decline?token=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: declineReason }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error?.message || 'Failed to decline');
        setIsSubmitting(false);
        return;
      }

      setIsDeclined(true);
      setShowDeclineModal(false);
      setIsSubmitting(false);
    } catch (err) {
      console.error('Error declining:', err);
      setError('Failed to decline. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Handle acknowledgment
  const handleAcknowledge = () => {
    setHasAcknowledged(true);
    setShowAcknowledgment(false);
  };

  const handleNotMe = () => {
    setShowAcknowledgment(false);
    setShowContactSupport(true);
  };

  const handleContactSupportClose = () => {
    setShowContactSupport(false);
    setShowAcknowledgment(true);
  };

  // Get full signer name
  const getSignerFullName = () => {
    if (!documentData) return '';
    const { firstName, lastName } = documentData.contact;
    return `${firstName}${lastName ? ' ' + lastName : ''}`;
  };

  // Render based on state
  if (isLoading) {
    return <LoadingPage message="Loading your document..." />;
  }

  if (tokenStatus === 'not_found') {
    return <NotFoundPage type="document" />;
  }

  if (tokenStatus === 'expired') {
    return <ExpiredPage type="document" />;
  }

  if (tokenStatus === 'already_completed' || isDeclined) {
    return (
      <CompletedPage
        type={isDeclined ? 'declined' : 'signed'}
        documentName={documentData?.document?.name}
        downloadUrl={downloadUrl || undefined}
      />
    );
  }

  if (error) {
    return (
      <PortalLayout>
        <div className="flex flex-1 items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Error</h1>
              <p className="mt-3 text-gray-600">{error}</p>
              <Button
                variant="outline"
                className="mt-6"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  if (!documentData) {
    return <NotFoundPage type="document" />;
  }

  // Success state
  if (isSuccess) {
    return (
      <PortalLayout>
        <div className="flex flex-1 items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md"
          >
            <Card>
              <CardContent className="p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                  className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100"
                >
                  <CheckCircle2 className="h-10 w-10 text-green-600" />
                </motion.div>

                <h1 className="text-2xl font-bold text-gray-900">
                  Document Signed!
                </h1>

                <p className="mt-3 text-gray-600">
                  Thank you, {documentData.contact.firstName}. Your signature has
                  been applied successfully.
                </p>

                <p className="mt-2 text-sm text-gray-500">
                  A confirmation email with your signed document will be sent to{' '}
                  <span className="font-medium text-gray-700">{documentData.contact.email || 'your email'}</span>.
                </p>

                <div className="mt-8">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { window.close(); setTimeout(() => { if (!window.closed) window.location.href = '/'; }, 300); }}
                  >
                    Close this page
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </PortalLayout>
    );
  }

  // Main signing view
  return (
    <PortalLayout showFooter={false}>
      <div className="flex h-[calc(100vh-3.5rem)] flex-col lg:flex-row">
        {/* Document Viewer */}
        <div className="flex-1 overflow-hidden">
          <DocumentViewer
            fileUrl={documentData.document.fileUrl}
            pageCount={documentData.document.pageCount}
            signatureFields={documentData.document.signatureFields}
            textFields={documentData.document.textFields}
            onSignatureFieldClick={handleSignatureFieldClick}
            signedFields={signedFields}
            setSignedFields={setSignedFields}
          />
        </div>

        {/* Desktop Sidebar (hidden on mobile) */}
        <div className="hidden w-full bg-white p-4 lg:block lg:w-80 lg:border-l">
          <div className="flex flex-col gap-4">
            <SignerInfoPanel
              document={documentData.document}
              contact={documentData.contact}
              signerContext={documentData.signerContext}
              signedCount={signedFields.size}
              requiredCount={
                documentData.document.signatureFields.filter((f) => f.required)
                  .length
              }
              totalFields={documentData.document.signatureFields.length}
            />

            <SignerFieldList
              fields={documentData.document.signatureFields}
              signedFields={signedFields}
              setSignedFields={setSignedFields}
              onSignatureFieldClick={handleSignatureFieldClick}
            />

            <div className="space-y-2 pt-4">
              <Button
                variant="default"
                className="w-full"
                disabled={!allRequiredFieldsSigned() || isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Complete Signing
              </Button>

              <Button
                variant="ghost"
                className="w-full text-gray-500 hover:text-red-600"
                onClick={() => setShowDeclineModal(true)}
                disabled={isSubmitting}
              >
                <X className="mr-2 h-4 w-4" />
                Decline to Sign
              </Button>
            </div>

            {documentData.expiresAt && (
              <p className="text-center text-xs text-gray-400">
                This link expires on{' '}
                {new Date(documentData.expiresAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        {/* Mobile Action Bar (hidden on desktop) */}
        <div className="order-last lg:hidden">
          <MobileSigningBar
            signedCount={signedFields.size}
            requiredCount={
              documentData.document.signatureFields.filter((f) => f.required)
                .length
            }
            canSubmit={allRequiredFieldsSigned()}
            isSubmitting={isSubmitting}
            onComplete={handleSubmit}
            onDecline={() => setShowDeclineModal(true)}
            onOpenDetails={() => setMobileDrawerOpen(true)}
          />
        </div>
      </div>

      {/* Mobile Drawer */}
      <MobileSigningDrawer
        open={mobileDrawerOpen}
        onOpenChange={setMobileDrawerOpen}
        documentData={documentData}
        signedFields={signedFields}
        setSignedFields={setSignedFields}
        onSignatureFieldClick={(field) => {
          setMobileDrawerOpen(false);
          handleSignatureFieldClick(field);
        }}
      />

      {/* Signature Capture Modal */}
      <SignatureCapture
        isOpen={!!activeField}
        onClose={() => setActiveField(null)}
        onSave={handleSignatureSave}
        fieldLabel={activeField?.label}
        signerName={getSignerFullName()}
      />

      {/* Decline Modal */}
      <AnimatePresence>
        {showDeclineModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowDeclineModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900">
                Decline to Sign?
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Please provide a reason for declining this signature request.
                The sender will be notified.
              </p>

              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Enter your reason..."
                rows={3}
                className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />

              <div className="mt-4 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeclineModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleDecline}
                  disabled={!declineReason.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <X className="mr-2 h-4 w-4" />
                  )}
                  Decline
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Signer Acknowledgment Modal */}
      {documentData && showAcknowledgment && !hasAcknowledged && (
        <SignerAcknowledgment
          isOpen={true}
          onConfirm={handleAcknowledge}
          onNotMe={handleNotMe}
          signerInfo={{
            name: getSignerFullName(),
            email: documentData.contact.email,
          }}
          documentInfo={{
            name: documentData.document.name,
          }}
        />
      )}

      {/* Contact Support Modal */}
      <ContactSupportModal
        isOpen={showContactSupport}
        onClose={handleContactSupportClose}
      />
    </PortalLayout>
  );
}
